import { ROUTES } from "@/routes"
import { create } from "zustand"
import { persist } from "zustand/middleware"

export interface WalkthroughStep {
  id: string
  title: string
  description: string
  target?: string // CSS selector for element to highlight
  position: "center" | "top" | "bottom" | "left" | "right"
  icon: string
  action?: "navigate" | "click" | "highlight"
  navigateTo?: string
}

interface WalkthroughStore {
  // State
  isActive: boolean
  currentStep: number
  hasCompletedWalkthrough: boolean
  isFirstLogin: boolean

  // Actions
  startWalkthrough: () => void
  nextStep: () => void
  previousStep: () => void
  skipWalkthrough: () => void
  completeWalkthrough: () => void
  resetWalkthrough: () => void
  setFirstLogin: (isFirst: boolean) => void
  goToStep: (step: number) => void
}

export const walkthroughSteps: WalkthroughStep[] = [
  {
    id: "welcome",
    title: "Welcome to CloudSpry! 🎉",
    description:
      "We're excited to have you on board! Let us show you around and help you get the most out of your cloud cost management experience.",
    position: "center",
    icon: "rocket",
  },
  {
    id: "dashboard",
    title: "Your Command Center 📊",
    description:
      "This is your Dashboard - your central hub for monitoring cloud costs, usage trends, and key metrics at a glance. Everything you need to stay on top of your cloud spending.",
    target: "[data-walkthrough='dashboard']",
    position: "right",
    icon: "dashboard",
    action: "navigate",
    navigateTo: ROUTES.dashboard.root.path,
  },
  {
    id: "sidebar",
    title: "Navigation Made Easy 🧭",
    description:
      "Use the sidebar to navigate between different sections. You can collapse it for more screen space or expand it for easy access to all features.",
    target: "[data-walkthrough='sidebar']",
    position: "right",
    icon: "menu",
  },
  {
    id: "cloud-manager",
    title: "Cloud Manager ☁️",
    description:
      "Manage your cloud resources, set up cost alerts, monitor performance, and generate detailed reports. Your complete toolkit for cloud infrastructure management.",
    target: "[data-walkthrough='cloud-manager']",
    position: "right",
    icon: "cloud",
  },
  {
    id: "cloud-allocation",
    title: "Cloud Allocation 🏢",
    description:
      "Organize your cloud resources by departments and team members. Allocate budgets, track usage per team, and ensure accountability across your organization.",
    target: "[data-walkthrough='cloud-allocation']",
    position: "right",
    icon: "building",
  },
  {
    id: "cloud-advisor",
    title: "Cloud Advisor 💡",
    description:
      "Get AI-powered recommendations to optimize costs, improve performance, and enhance security. CloudSpry analyzes your usage patterns and suggests actionable improvements.",
    target: "[data-walkthrough='cloud-advisor']",
    position: "right",
    icon: "lightbulb",
  },
  {
    id: "billing",
    title: "Billing 💳",
    description:
      "Track your billing, manage invoices, set up payment methods, and monitor your AWS account expenses. Stay in control of your cloud spending.",
    target: "[data-walkthrough='billing']",
    position: "right",
    icon: "wallet",
  },
  {
    id: "my-account",
    title: "Your Profile 👤",
    description:
      "View and manage your account details, update personal information, check your subscription plan, and review your payment history. Your account, your control.",
    target: "[data-walkthrough='my-account']",
    position: "right",
    icon: "user",
  },
  {
    id: "settings",
    title: "Personalize Your Experience ⚙️",
    description:
      "Customize your preferences, configure notifications, manage security settings, and connect your AWS accounts. Make CloudSpry work exactly the way you want.",
    target: "[data-walkthrough='settings']",
    position: "right",
    icon: "settings",
  },
  {
    id: "signout",
    title: "Sign Out Safely 🔐",
    description:
      "When you're done, use the Sign Out button at the bottom of the sidebar to securely log out of your account. Your data is always safe with us.",
    target: "[data-walkthrough='signout']",
    position: "right",
    icon: "logout",
  },
  {
    id: "complete",
    title: "You're All Set! 🚀",
    description:
      "Congratulations! You're ready to start optimizing your cloud costs. If you ever need help, check out our documentation or reach out to support. Happy cloud managing!",
    position: "center",
    icon: "celebration",
  },
]

export const useWalkthroughStore = create<WalkthroughStore>()(
  persist(
    (set, get) => ({
      // Initial state
      isActive: false,
      currentStep: 0,
      hasCompletedWalkthrough: false,
      isFirstLogin: false,

      // Actions
      startWalkthrough: () =>
        set({
          isActive: true,
          currentStep: 0,
        }),

      nextStep: () => {
        const { currentStep } = get()
        if (currentStep < walkthroughSteps.length - 1) {
          set({ currentStep: currentStep + 1 })
        } else {
          get().completeWalkthrough()
        }
      },

      previousStep: () => {
        const { currentStep } = get()
        if (currentStep > 0) {
          set({ currentStep: currentStep - 1 })
        }
      },

      skipWalkthrough: () =>
        set({
          isActive: false,
          hasCompletedWalkthrough: true,
          isFirstLogin: false,
        }),

      completeWalkthrough: () =>
        set({
          isActive: false,
          hasCompletedWalkthrough: true,
          isFirstLogin: false,
          currentStep: 0,
        }),

      resetWalkthrough: () =>
        set({
          isActive: false,
          currentStep: 0,
          hasCompletedWalkthrough: false,
        }),

      setFirstLogin: (isFirst) => set({ isFirstLogin: isFirst }),

      goToStep: (step) => {
        if (step >= 0 && step < walkthroughSteps.length) {
          set({ currentStep: step })
        }
      },
    }),
    {
      name: "cloudspry-walkthrough-storage",
      partialize: (state) => ({
        hasCompletedWalkthrough: state.hasCompletedWalkthrough,
      }),
    }
  )
)
